import {COLORS} from 'frontend/utils/color_palettes';

interface DoubleCircleIconSize {
  size: number;
  innerRadius?: number;
  outerRadius?: number;
}

export enum MapIconSize {
  small = 20,
  medium = 24,
  large = 32,
}

const SVG = 'xmlns="http://www.w3.org/2000/svg"';

const DEFAULT_PRIMARY_BLUE_COLOR = COLORS.Blue.get(700);

const DEFAULT_STROKE_COLOR = DEFAULT_PRIMARY_BLUE_COLOR;

const DEFAULT_MAP_MARKER_STROKE_COLOR = COLORS.NewPrimary.get(99);

const BATTERY_GENERATOR_MARKER_FILL_COLOR = COLORS.NewIndigo.get(50);
const BIOMASS_GENERATOR_MARKER_FILL_COLOR = COLORS.NewGreen.get(50);
const COAL_GENERATOR_MARKER_FILL_COLOR = COLORS.NewPrimary.get(30);
const CSP_SOLAR_GENERATOR_MARKER_FILL_COLOR = COLORS.NewOlive.get(50);
const DIESEL_GENERATOR_MARKER_FILL_COLOR = COLORS.NewMagenta.get(50);
const FUEL_OIL_GENERATOR_MARKER_FILL_COLOR = COLORS.NewOrange.get(50);
const GEO_THERMAL_GENERATOR_MARKER_FILL_COLOR = COLORS.NewLime.get(50);
const HYDRO_GENERATOR_MARKER_FILL_COLOR = COLORS.NewBlue.get(50);
const NATURAL_GAS_GENERATOR_MARKER_FILL_COLOR = COLORS.NewRed.get(50);
const PHOTO_VOTAIC_SOLAR_GENERATOR_MARKER_FILL_COLOR = COLORS.NewYellow.get(50);
const PUMPED_HYDRO_STORAGE_GENERATOR_MARKER_FILL_COLOR = COLORS.NewSteelBlue.get(50);
const WIND_GENERATOR_MARKER_FILL_COLOR = COLORS.NewCyan.get(50);

/** Circle icon with one color. */
export function circleIcon(color: string, size: number): google.maps.Icon {
  const radius = size / 2;

  return buildIcon(`<circle r="${radius}" cx="${radius}" cy="${radius}" fill="${color}"/>`, size);
}

/**
 * Circle icon with smaller circle inside.
 * @param fillColor color of the inner circle.
 * @param strokeColor color of the outer circle.
 */
export function doubleCircleIconSVG(
  fillColor: string,
  strokeColor: string,
  size: DoubleCircleIconSize,
): string {
  const innerRadius = size.innerRadius || size.size / 4;
  const outerRadius = size.outerRadius || size.size / 2.5;
  const center = size.size / 2;

  return buildSVG(
    `<circle r="${outerRadius}" cy="${center}" cx="${center}" fill="${strokeColor}"/>
     <circle r="${innerRadius}" cy="${center}" cx="${center}" fill="${fillColor}"/>`,
    size.size,
  );
}

/**
 * Circle icon with smaller circle inside.
 * @param fillColor color of the inner circle.
 * @param strokeColor color of the outer circle.
 */
export function doubleCircleIcon(
  fillColor: string,
  strokeColor: string,
  size: DoubleCircleIconSize,
): google.maps.Icon {
  return buildIconFromSVG(doubleCircleIconSVG(fillColor, strokeColor, size));
}

/**
 * Location icon.
 */
export function locationSvg(fill = COLORS.NewPrimary.get(40), size = 40): string {
  const patchShape =
    'M446.67-406.67h66.66v-120h120v-66.66h-120v-120h-66.66v120h-120v66.66h120v120ZM480-168q129.33-118 191.33-214.17 62-96.16 62-169.83 0-114.86-73.36-188.1-73.36-73.23-179.97-73.23T300.03-740.1q-73.36 73.24-73.36 188.1 0 73.67 63 169.83Q352.67-286 480-168Zm0 88Q319-217 239.5-334.5T160-552q0-150 96.5-239T480-880q127 0 223.5 89T800-552q0 100-79.5 217.5T480-80Zm0-480Z';
  return `
    <svg ${SVG} height="${size}" width="${size}" fill="${fill}" viewBox="0 -960 960 960">
      <path d="${patchShape}"/>
    </svg>
  `;
}

/**
 * Model assets icon.
 */
export function modelAssetsSVG(strokeColor = '#757575', size = 28): string {
  const linePath = [
    'M17.6498 12.0317C17.6672 10.016 16.0474 8.36788 14.0317 8.35043C12.016 8.33298 10.3679 9.95285 10.3504 11.9685C10.333 13.9842 11.9529 15.6323 13.9685 15.6498C15.9842 15.6672 17.6323 14.0473 17.6498 12.0317Z',
    'M27.2616 11.1045L24.4065 6.12134H18.906L22.2873 12L15.3813 24H19.85C20.495 24 21.0906 23.655 21.4115 23.0955L27.2615 12.8955C27.5794 12.3408 27.5795 11.6592 27.2616 11.1045Z',
    'M16.6345 19.2H9.85573L2.96473 7.224L0.738427 11.1045C0.420524 11.6592 0.420524 12.3408 0.738427 12.8955L6.58843 23.0955C6.90932 23.655 7.50507 24.0001 8.15008 24H13.863L16.6345 19.2Z',
    'M21.4113 0.9045C21.0905 0.34507 20.4949 3.05105e-05 19.85 0H8.14997C7.50501 -2.32335e-05 6.90933 0.345024 6.58847 0.9045L3.71387 5.91105L6.46742 10.68L9.85562 4.8H23.645L21.4113 0.9045Z',
  ]
    .map(
      (path: string) =>
        `<path fill-rule="evenodd" clip-rule="evenodd" d="${path}" fill="${strokeColor}"/>`,
    )
    .join('');

  return `
<svg ${SVG} width="${size}" height="${size}" viewBox="0 0 28 24" fill="none">
  ${linePath}
</svg>`;
}

/**
 * Generator icon.
 */
export function generatorSVG({
  strokeColor = DEFAULT_STROKE_COLOR,
  fillColor = 'none',
  size = 16,
}): string {
  const pathGLetter =
    'M8.1108 12C7.5494 12 7.01754 11.9002 6.51524 11.7005C6.02031 11.5009 5.58449 11.2193 5.20776 10.8556C4.83103 10.492 4.53555 10.0677 4.32133 9.58289C4.10711 9.09804 4 8.57041 4 8C4 7.42959 4.10711 6.90196 4.32133 6.41711C4.53555 5.93226 4.83103 5.50802 5.20776 5.14439C5.58449 4.78075 6.02031 4.49911 6.51524 4.29947C7.01754 4.09982 7.5494 4 8.1108 4C8.69437 4 9.241 4.09982 9.75069 4.29947C10.2678 4.49911 10.6925 4.78075 11.0249 5.14439L10.3601 5.7861C10.1902 5.58645 9.98707 5.41889 9.75069 5.28342C9.51431 5.14795 9.25946 5.04456 8.98615 4.97326C8.71284 4.90196 8.42475 4.86631 8.12188 4.86631C7.70083 4.86631 7.29825 4.94118 6.91413 5.09091C6.53001 5.23351 6.19021 5.44385 5.89474 5.72193C5.60665 5.99287 5.37765 6.32086 5.20776 6.70588C5.03786 7.09091 4.95291 7.52228 4.95291 8C4.95291 8.47772 5.03786 8.90909 5.20776 9.29412C5.38504 9.67914 5.62142 10.0107 5.9169 10.2888C6.21237 10.5597 6.54848 10.7701 6.92521 10.9198C7.30933 11.0624 7.70822 11.1337 8.12188 11.1337C8.47645 11.1337 8.81994 11.0873 9.15236 10.9947C9.49215 10.8948 9.79501 10.7451 10.0609 10.5455C10.3343 10.3387 10.5596 10.0749 10.7368 9.75401C10.9141 9.43316 11.0212 9.04813 11.0582 8.59893H8.13296V7.75401H11.9446C11.9594 7.8467 11.9705 7.93583 11.9778 8.02139C11.9926 8.10695 12 8.19964 12 8.29947V8.31016C12 8.85918 11.904 9.36185 11.7119 9.81818C11.5199 10.2674 11.2502 10.656 10.903 10.984C10.5559 11.3048 10.1459 11.5544 9.67313 11.7326C9.20037 11.9109 8.67959 12 8.1108 12Z';

  return `
<svg ${SVG} width="${size}" height="${size}" viewBox="0 0 16 16" fill="none">
  <circle cy="8" cx="8" r="8" fill="${fillColor}"></circle>
  <path d="${pathGLetter}" fill="${strokeColor}"/>
  <circle cx="8" cy="8" r="6.5" stroke="${strokeColor}"/>
</svg>`;
}

/**
 * Battery generator icon.
 */
export function batteryGeneratorSVG({
  strokeColor = DEFAULT_MAP_MARKER_STROKE_COLOR,
  fillColor = BATTERY_GENERATOR_MARKER_FILL_COLOR,
  size = 16,
  iconOnly = false,
}) {
  const path =
    'M320-80q-17 0-28.5-11.5T280-120v-640q0-17 11.5-28.5T320-800h80v-80h160v80h80q17 0 28.5 11.5T680-760v640q0 17-11.5 28.5T640-80H320Zm40-80h240v-560H360v560Z';

  return `
  <svg ${SVG} height="${size}" width="${size}" fill="${
    iconOnly ? fillColor : strokeColor
  }" viewBox="0 -1000 1000 1000">
    ${iconOnly ? '' : `<circle cx="500" cy="-500" r="500" fill="${fillColor}" />`}
    <path d="${path}"/>
  </svg>
  `;
}

/**
 * Biomass generator icon.
 */
export function bioMassGeneratorSVG({
  strokeColor = DEFAULT_MAP_MARKER_STROKE_COLOR,
  fillColor = BIOMASS_GENERATOR_MARKER_FILL_COLOR,
  size = 16,
  iconOnly = false,
}) {
  const path =
    'M480-100q-79 0-148-30t-120.5-81.5Q160-263 130-332t-30-148q0-79 30-148t81.5-120.5Q263-800 332-830t148-30v-100l160 160-160 160v-100q-108 0-184 76t-76 184q0 66 30.5 122.5T332-266q16-28 47.5-47.5T452-338q-3-21-8-42t-12-39q-11 9-24 14t-28 5q-33 0-56.5-23.5T300-480v-40q0-17-5.5-32T280-580q50-1 89 9 34 9 62 29.5t29 61.5q0 9-1.5 16.5T453-448q-13-10-26-18t-27-14q17 13 39 40t41 64q20-49 50-96.5t70-87.5q-23 16-44 34t-41 38q-7-11-11-24.5t-4-27.5q0-42 29-71t71-29h40q23 0 38-6t25-14q11-9 17-20 4 67-7 120-9 45-34 82.5T600-440q-15 0-28.5-4T547-455q-7 19-16 50.5T517-337q38 7 67 26t44 45q51-35 81.5-91T740-480h120q0 79-30 148t-81.5 120.5Q697-160 628-130t-148 30Z';

  return `
  <svg ${SVG} height="${size}" width="${size}" fill="${
    iconOnly ? fillColor : strokeColor
  }" viewBox="0 -1000 1000 1000">
    ${iconOnly ? '' : `<circle cx="500" cy="-500" r="500" fill="${fillColor}" />`}
    <path d="${path}"/>
  </svg>
  `;
}

/**
 * Coal generator icon.
 */
export function coalGeneratorSVG({
  strokeColor = DEFAULT_MAP_MARKER_STROKE_COLOR,
  fillColor = COAL_GENERATOR_MARKER_FILL_COLOR,
  size = 16,
  iconOnly = false,
}) {
  const path =
    'M80-80v-481l280-119v80l200-80v120h320v480H80Zm80-80h640v-320H480v-82l-200 80v-78l-120 53v347Zm280-80h80v-160h-80v160Zm-160 0h80v-160h-80v160Zm320 0h80v-160h-80v160Zm280-320H680l40-320h120l40 320ZM160-160h640-640Z';

  return `
  <svg ${SVG} height="${size}" width="${size}" viewBox="0 -1000 1000 1000" fill="${
    iconOnly ? fillColor : strokeColor
  }">
    ${iconOnly ? '' : `<circle cx="500" cy="-500" r="500" fill="${fillColor}" />`}
     <path d="${path}"/>
  </svg>
  `;
}

/**
 * CSP Solar generator icon.
 */
export function cspSolarGeneratorSVG({
  strokeColor = DEFAULT_MAP_MARKER_STROKE_COLOR,
  fillColor = CSP_SOLAR_GENERATOR_MARKER_FILL_COLOR,
  size = 16,
  iconOnly = false,
}) {
  const iconColor = iconOnly ? fillColor : strokeColor;
  return `
  <svg ${SVG} height="${size}" width="${size}" fill="${iconColor}" viewBox="0 0 20 20">
    ${iconOnly ? '' : `<circle cx="10" cy="10" r="10" fill="${fillColor}" />`}
    <rect x="9" y="19" width="16" height="2" transform="rotate(-90 9 19)" fill="${iconColor}"/>
    <rect x="18" y="19" width="16" height="4" transform="rotate(180 18 19)" fill="${iconColor}"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M12 3C12 1.89543 11.1046 1 10 1C8.89543 1 8 1.89543 8 3C8 4.10457 8.89543 5 10 5C11.1046 5 12 4.10457 12 3Z" fill="${iconColor}"/>
    <path d="M8.90885 3.0001L4.04189 9.95084" stroke="${iconColor}"/>
    <path d="M11.2874 3.0001L16.1544 9.95084" stroke="${iconColor}"/>
    <path d="M9.19615 3L7 11.1962" stroke="${iconColor}"/>
    <path d="M11.0001 3L13.1963 11.1962" stroke="${iconColor}"/>
    <path d="M8.95084 3.04189L2.0001 7.90885" stroke="${iconColor}"/>
    <path d="M11.2455 3.04189L18.1962 7.90885" stroke="${iconColor}"/>
    <path d="M9.19615 3L1 5.19615" stroke="${iconColor}"/>
    <path d="M11.0001 3L19.1963 5.19615" stroke="${iconColor}"/>
  </svg>
  `;
}

/**
 * Diesel generator icon.
 */
export function dieselGeneratorSVG({
  strokeColor = DEFAULT_MAP_MARKER_STROKE_COLOR,
  fillColor = DIESEL_GENERATOR_MARKER_FILL_COLOR,
  size = 16,
  iconOnly = false,
}) {
  const path =
    'M160-120q-17 0-28.5-11.5T120-160q0-17 11.5-28.5T160-200h40v-240h-40q-17 0-28.5-11.5T120-480q0-17 11.5-28.5T160-520h40v-240h-40q-17 0-28.5-11.5T120-800q0-17 11.5-28.5T160-840h640q17 0 28.5 11.5T840-800q0 17-11.5 28.5T800-760h-40v240h40q17 0 28.5 11.5T840-480q0 17-11.5 28.5T800-440h-40v240h40q17 0 28.5 11.5T840-160q0 17-11.5 28.5T800-120H160Zm120-80h400v-240q-17 0-28.5-11.5T640-480q0-17 11.5-28.5T680-520v-240H280v240q17 0 28.5 11.5T320-480q0 17-11.5 28.5T280-440v240Zm200-120q50 0 85-34.5t35-83.5q0-39-22.5-67T480-620q-75 86-97.5 114.5T360-438q0 49 35 83.5t85 34.5ZM280-200v-560 560Z';

  return `
  <svg ${SVG} height="${size}" width="${size}" fill="${
    iconOnly ? fillColor : strokeColor
  }" viewBox="0 -1000 1000 1000">
    ${iconOnly ? '' : `<circle cx="500" cy="-500" r="500" fill="${fillColor}" />`}
    <path d="${path}"/>
  </svg>
  `;
}

/**
 * Fuel oil generator icon.
 */
export function fuelOilGeneratorSVG({
  strokeColor = DEFAULT_MAP_MARKER_STROKE_COLOR,
  fillColor = FUEL_OIL_GENERATOR_MARKER_FILL_COLOR,
  size = 16,
  iconOnly = false,
}) {
  const path =
    'M160-120q-17 0-28.5-11.5T120-160q0-17 11.5-28.5T160-200h40v-240h-40q-17 0-28.5-11.5T120-480q0-17 11.5-28.5T160-520h40v-240h-40q-17 0-28.5-11.5T120-800q0-17 11.5-28.5T160-840h640q17 0 28.5 11.5T840-800q0 17-11.5 28.5T800-760h-40v240h40q17 0 28.5 11.5T840-480q0 17-11.5 28.5T800-440h-40v240h40q17 0 28.5 11.5T840-160q0 17-11.5 28.5T800-120H160Zm320-200q50 0 85-34.5t35-83.5q0-39-22.5-67T480-620q-75 86-97.5 114.5T360-438q0 49 35 83.5t85 34.5Z';

  return `
  <svg ${SVG} height="${size}" width="${size}" fill="${
    iconOnly ? fillColor : strokeColor
  }" viewBox="0 -1000 1000 1000">
    ${iconOnly ? '' : `<circle cx="500" cy="-500" r="500" fill="${fillColor}" />`}
    <path d="${path}"/>
  </svg>
  `;
}

/**
 * Geothermal generator icon.
 */
export function geoThermalGeneratorSVG({
  strokeColor = DEFAULT_MAP_MARKER_STROKE_COLOR,
  fillColor = GEO_THERMAL_GENERATOR_MARKER_FILL_COLOR,
  size = 16,
  iconOnly = false,
}) {
  const path =
    'M532-131q-6 5-12.5 8t-14.5 3q-8 0-16-3.5t-14-9.5q-41-44-60.5-90T395-320q0-37 11-78t38-106q23-57 32-87.5t9-56.5q0-34-15-63.5T423-771q-6-6-9.5-14t-3.5-16q0-8 3-14.5t8-12.5q6-6 13.5-9t15.5-3q8 0 15 3t13 8q44 41 65.5 86t21.5 95q0 35-10.5 73.5T518-474q-25 60-34 92t-9 61q0 35 14.5 67.5T534-188q5 6 8 13t3 15q0 8-3 15.5T532-131Zm195 0q-6 5-12.5 8t-14.5 3q-8 0-16-3.5t-14-9.5q-41-44-60.5-89.5T590-319q0-37 11-79t38-106q23-57 32-87t9-56q0-34-15-64.5T618-771q-6-6-9-13.5t-3-15.5q0-8 2.5-14.5T616-827q6-6 14-9.5t16-3.5q8 0 14.5 3t12.5 8q44 41 65.5 86t21.5 95q0 35-10.5 73.5T713-473q-25 60-34 92t-9 60q0 35 15 68.5t45 65.5q5 6 7.5 13t2.5 14q0 8-3 16t-10 13Zm-390 0q-6 5-12.5 8t-14.5 3q-8 0-16-3.5t-14-9.5q-41-44-60.5-89.5T200-319q0-37 11-79t38-106q23-57 32-87t9-56q0-34-15-64.5T228-771q-7-6-10-13.5t-3-15.5q0-8 3-15t8-13q6-6 13.5-9t15.5-3q8 0 15 3t13 8q44 41 65.5 85.5T370-648q0 35-10 73.5T324-474q-25 60-34 92t-9 61q0 35 14.5 68.5T340-187q5 6 7.5 13t2.5 14q0 8-3 16t-10 13Z';

  return `
  <svg ${SVG} height="${size}" width="${size}" fill="${
    iconOnly ? fillColor : strokeColor
  }" viewBox="0 -1000 1000 1000">
    ${iconOnly ? '' : `<circle cx="500" cy="-500" r="500" fill="${fillColor}" />`}
    <path d="${path}"/>
  </svg>
  `;
}

/**
 * Hydro generator icon.
 */
export function hydroGeneratorSVG({
  strokeColor = DEFAULT_MAP_MARKER_STROKE_COLOR,
  fillColor = HYDRO_GENERATOR_MARKER_FILL_COLOR,
  size = 16,
  iconOnly = false,
}) {
  const path =
    'M80-240v-80q38 0 56.5-20t77.5-20q59 0 77.5 20t54.5 20q38 0 56.5-20t77.5-20q57 0 77.5 20t56.5 20q38 0 55.5-20t76.5-20q59 0 77.5 20t56.5 20v80q-57 0-77.5-20T746-280q-36 0-54.5 20T614-240q-57 0-77.5-20T480-280q-38 0-56.5 20T346-240q-59 0-76.5-20T214-280q-38 0-56.5 20T80-240Zm0-160v-80q38 0 56.5-20t77.5-20q57 0 76.5 20t55.5 20q38 0 56.5-20t77.5-20q57 0 77 20t55 20q38 0 56.5-20t77.5-20q57 0 77.5 20t56.5 20v80q-59 0-78.5-20T746-440q-36 0-54.5 20T614-400q-57 0-77.5-20T480-440q-38 0-55.5 20T348-400q-59 0-78.5-20T214-440q-36 0-56.5 20T80-400Zm0-160v-80q38 0 56.5-20t77.5-20q57 0 76.5 20t55.5 20q38 0 56.5-20t77.5-20q57 0 77 20t55 20q38 0 56.5-20t77.5-20q57 0 77.5 20t56.5 20v80q-59 0-78.5-20T746-600q-36 0-54.5 20T614-560q-57 0-77.5-20T480-600q-38 0-55.5 20T348-560q-59 0-78.5-20T214-600q-36 0-56.5 20T80-560Z';

  return `
  <svg ${SVG} height="${size}" width="${size}" fill="${
    iconOnly ? fillColor : strokeColor
  }" viewBox="0 -1000 1000 1000">
    ${iconOnly ? '' : `<circle cx="500" cy="-500" r="500" fill="${fillColor}" />`}
    <path d="${path}"/>
  </svg>
  `;
}

/**
 * Natural gas generator icon.
 */
export function naturalGasGeneratorSVG({
  strokeColor = DEFAULT_MAP_MARKER_STROKE_COLOR,
  fillColor = NATURAL_GAS_GENERATOR_MARKER_FILL_COLOR,
  size = 16,
  iconOnly = false,
}) {
  const path =
    'M240-400q0 52 21 98.5t60 81.5q-1-5-1-9v-9q0-32 12-60t35-51l113-111 113 111q23 23 35 51t12 60v9q0 4-1 9 39-35 60-81.5t21-98.5q0-50-18.5-94.5T648-574q-20 13-42 19.5t-45 6.5q-62 0-107.5-41T401-690q-39 33-69 68.5t-50.5 72Q261-513 250.5-475T240-400Zm240 52-57 56q-11 11-17 25t-6 29q0 32 23.5 55t56.5 23q33 0 56.5-23t23.5-55q0-16-6-29.5T537-292l-57-56Zm0-492v132q0 34 23.5 57t57.5 23q18 0 33.5-7.5T622-658l18-22q74 42 117 117t43 163q0 134-93 227T480-80q-134 0-227-93t-93-227q0-129 86.5-245T480-840Z';

  return `
  <svg ${SVG} height="${size}" width="${size}" fill="${
    iconOnly ? fillColor : strokeColor
  }" viewBox="0 -1000 1000 1000">
    ${iconOnly ? '' : `<circle cx="500" cy="-500" r="500" fill="${fillColor}" />`}
    <path d="${path}"/>
  </svg>
  `;
}

/**
 * Photo voltaic solar generator icon.
 */
export function photoVoltaicSolarGeneratorSVG({
  strokeColor = DEFAULT_MAP_MARKER_STROKE_COLOR,
  fillColor = PHOTO_VOTAIC_SOLAR_GENERATOR_MARKER_FILL_COLOR,
  size = 16,
  iconOnly = false,
}) {
  const path =
    'm80-80 80-400h640l80 400H80Zm40-720v-80h120v80H120Zm58 640h262v-80H194l-16 80Zm67-427-57-56 85-85 57 56-85 85Zm-35 267h230v-80H226l-16 80Zm270-360q-83 0-141.5-58.5T280-880h80q0 50 35 85t85 35q50 0 85-35t35-85h80q0 83-58.5 141.5T480-680Zm0-200Zm-40 360v-120h80v120h-80Zm80 360h262l-16-80H520v80Zm0-160h230l-16-80H520v80Zm195-267-84-85 56-56 85 84-57 57Zm5-213v-80h120v80H720Z';

  return `
  <svg ${SVG} height="${size}" width="${size}" fill="${
    iconOnly ? fillColor : strokeColor
  }" viewBox="0 -1000 1000 1000">
    ${iconOnly ? '' : `<circle cx="500" cy="-500" r="500" fill="${fillColor}" />`}
    <path d="${path}"/>
  </svg>
  `;
}

/**
 * Pumped hydro storage generator icon.
 */
export function pumpedHydroStorageGeneratorSVG({
  strokeColor = DEFAULT_MAP_MARKER_STROKE_COLOR,
  fillColor = PUMPED_HYDRO_STORAGE_GENERATOR_MARKER_FILL_COLOR,
  size = 16,
  iconOnly = false,
}) {
  const path =
    'M120-280h164q-17-17-31.5-37T227-360H120v80Zm360 0q83 0 141.5-58.5T680-480q0-83-58.5-141.5T480-680q-83 0-141.5 58.5T280-480q0 83 58.5 141.5T480-280Zm253-320h107v-80H676q17 17 31.5 37t25.5 43ZM40-160v-320h80v40h83q-2-10-2.5-19.5T200-480q0-117 81.5-198.5T480-760h360v-40h80v320h-80v-40h-83q2 10 2.5 19.5t.5 20.5q0 117-81.5 198.5T480-200H120v40H40Zm80-120v-80 80Zm720-320v-80 80ZM480-480Zm0 120q-33 0-56.5-23.5T400-440q0-23 9.5-45.5T446-550l34-50 34 50q27 42 36.5 64.5T560-440q0 33-23.5 56.5T480-360Z';

  return `
  <svg ${SVG} height="${size}" width="${size}" fill="${
    iconOnly ? fillColor : strokeColor
  }" viewBox="0 -1000 1000 1000">
    ${iconOnly ? '' : `<circle cx="500" cy="-500" r="500" fill="${fillColor}" />`}
    <path d="${path}"/>
  </svg>
  `;
}

/**
 * Wind generator icon.
 */
export function windGeneratorSVG({
  strokeColor = DEFAULT_MAP_MARKER_STROKE_COLOR,
  fillColor = WIND_GENERATOR_MARKER_FILL_COLOR,
  size = 16,
  iconOnly = false,
}) {
  const path =
    'M400-40q0-33 23.5-56.5T480-120v-208q-12-5-22.5-11.5T438-354l-88 56q-14 8-30.5 10.5T286-290l-180-51q-29-8-47.5-32.5T40-429q0-38 26.5-64.5T131-520h301q10-11 22-19t26-13v-137q0-17 6.5-32t18.5-26l137-128q23-22 53.5-25t56.5 13q32 20 41.5 56.5T783-762L624-499q7 12 10.5 26t4.5 29l108 26q16 4 29 14t21 24l91 164q15 27 11 57t-26 52q-27 27-64.5 27T744-107L560-291v171q33 0 56.5 23.5T640-40H400ZM160-760v-80h240v80H160Zm400 71v137q1 0 1.5.5t1.5.5l152-253q2-4 1-8.5t-5-6.5q-3-2-7.5-1t-6.5 3L560-689ZM40-600v-80h200v80H40Zm480 200q17 0 28.5-11.5T560-440q0-17-11.5-28.5T520-480q-17 0-28.5 11.5T480-440q0 17 11.5 28.5T520-400Zm-211 34 93-56q-1-5-1-9v-9H131q-5 0-8 3t-3 8q0 4 2 7t6 4l181 52Zm419 25-114-26q-2 2-4 5t-4 5l195 194q3 3 8 3t8-3q3-3 3.5-6.5T819-177l-91-164ZM120-120v-80h200v80H120Zm400-320Zm43-111ZM401-440Zm205 83Z';

  return `
  <svg ${SVG} height="${size}" width="${size}" fill="${
    iconOnly ? fillColor : strokeColor
  }" viewBox="0 -1000 1000 1000">
    ${iconOnly ? '' : `<circle cx="500" cy="-500" r="500" fill="${fillColor}" />`}
    <path d="${path}"/>
  </svg>
  `;
}

/**
 * Tower icon.
 */
export function towerSVG(
  strokeColor = DEFAULT_STROKE_COLOR,
  fillColor = 'none',
  size = 16,
): string {
  return `
<svg ${SVG} width="${size}" height="${size}" viewBox="0 0 16 16" fill="none">
  <circle cy="8" cx="8" r="8" fill="${fillColor}"></circle>
  <path d="M4.34438 14.75L8 2.71974L11.6556 14.75H4.34438Z" stroke="${strokeColor}"/>
  <path d="M6.48952 6.5L3.60063 4.5H12.3994L9.51048 6.5H6.48952Z" stroke="${strokeColor}"/>
  <line x1="9.34855" y1="7.35849" x2="6.34871" y2="10.2752" stroke="${strokeColor}"/>
  <line x1="2.5" y1="4" x2="2.5" y2="6" stroke="${strokeColor}"/>
  <line x1="13.5" y1="4" x2="13.5" y2="6" stroke="${strokeColor}"/>
  <line x1="10" y1="10.5" x2="6" y2="10.5" stroke="${strokeColor}"/>
  <line x1="10.2928" y1="10.4053" x2="4.29291" y2="14.7387" stroke="${strokeColor}"/>
</svg>`;
}

/**
 * Transmission line icon.
 */
export function transmissionLineSVG(
  strokeColor = DEFAULT_STROKE_COLOR,
  fillColor = 'none',
  size = 16,
): string {
  return `
<svg ${SVG} width="${size}" height="${size}" viewBox="0 0 16 16" fill="none">
  <circle cy="8" cx="8" r="8" fill="${fillColor}"></circle>
  <path d="M0 8L16 8" stroke="${strokeColor}" stroke-width="4"/>
</svg>`;
}

/**
 * Substation icon.
 */
export function substationSVG(
  strokeColor = DEFAULT_STROKE_COLOR,
  fillColor = 'none',
  strokeWidth = 2,
  size = 16,
): string {
  return `
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 16 16" fill="none">
  <circle cx="8" cy="8" r="6" stroke="${strokeColor}" fill="${fillColor}" stroke-width="${strokeWidth}"/>
</svg>`;
}

/**
 * Feeder icon.
 */
export function feederSVG(
  strokeColor = DEFAULT_STROKE_COLOR,
  fillColor = 'none',
  strokeWidth = 1,
  size = 16,
  opacity = 1,
): string {
  return `
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 16 16" fill="none">
  <circle cx="8" cy="8" r="6" stroke="${strokeColor}" fill="${fillColor}" stroke-width="${strokeWidth}" opacity="${opacity}"/>
  <circle cx="8" cy="8" r="4" stroke="${strokeColor}" fill="${fillColor}" stroke-width="${strokeWidth}" opacity="${opacity}"/>
</svg>`;
}

/**
 * Opened switch icon.
 */
export function openedSwitchSVG(strokeColor = DEFAULT_STROKE_COLOR, size = 16): string {
  return `
<svg width="${size}" height="${size}" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
  <path fill="${strokeColor}" d="M7 5a1 1 0 0 0 2 0H7Zm0-5v5h2V0H7Z"/>
  <path stroke="${strokeColor}" stroke-width="2" d="M8 16v-4"/>
  <circle cx="8" cy="11" r="1.5" stroke="${strokeColor}"/>
  <path stroke="${strokeColor}" stroke-linecap="round" stroke-width="2" d="m9.5 9.5 3-3"/>
</svg>
`;
}

/**
 * Recloser icon.
 */
export function recloserSVG(fillColor = DEFAULT_STROKE_COLOR, size = 16): string {
  return `
  <svg width="${size}" height="${size}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 7H5V9H0V7Z" fill="${fillColor}"/>
    <path d="M11 7H16V9H11V7Z" fill="${fillColor}"/>
    <rect x="5.5" y="5.5" width="5" height="5" stroke="${fillColor}"/>
    <path d="M7.16513 9V6.81818H8.06641C8.22976 6.81818 8.37074 6.84766 8.48935 6.90661C8.60866 6.96484 8.70064 7.04865 8.76527 7.15803C8.8299 7.26669 8.86222 7.3956 8.86222 7.54474C8.86222 7.69602 8.82919 7.82457 8.76314 7.9304C8.69709 8.03551 8.60334 8.11577 8.48189 8.17116C8.36044 8.22585 8.21662 8.2532 8.05043 8.2532H7.48047V7.83771H7.95241C8.03196 7.83771 8.09837 7.82741 8.15163 7.80682C8.20561 7.78551 8.24645 7.75355 8.27415 7.71094C8.30185 7.66761 8.3157 7.61222 8.3157 7.54474C8.3157 7.47727 8.30185 7.42152 8.27415 7.37749C8.24645 7.33274 8.20561 7.29936 8.15163 7.27734C8.09766 7.25462 8.03125 7.24325 7.95241 7.24325H7.69247V9H7.16513ZM8.39347 8.00284L8.93679 9H8.36151L7.82884 8.00284H8.39347Z" fill="${fillColor}"/>
  </svg>
`;
}

/**
 * Breaker icon.
 */
export function breakerSVG(fillColor = DEFAULT_STROKE_COLOR, size = 16): string {
  return `
  <svg width="${size}" height="${size}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 7H5V9H0V7Z" fill="${fillColor}"/>
    <path d="M11 7H16V9H11V7Z" fill="${fillColor}"/>
    <rect x="5.5" y="5.5" width="5" height="5" stroke="${fillColor}"/>
  </svg>
`;
}

/**
 * Fuse icon.
 */
export function fuseSVG(fillColor = DEFAULT_STROKE_COLOR, size = 16): string {
  return `
  <svg width="${size}" height="${size}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 11H5V13H0V11Z" fill="${fillColor}"/>
    <path d="M5 11C5 11 6.5 8.49995 8 11C9.5 13.5 11 11 11 11V13C11 13 9.5 15.5 8 13C6.5 10.5 5 13 5 13V11Z" fill="${fillColor}"/>
    <path d="M11 11H16V13H11V11Z" fill="${fillColor}"/>
  </svg>
`;
}

/**
 * Cut icon.
 */
export function cutSVG(fillColor = DEFAULT_STROKE_COLOR, size = 16): string {
  return `
  <svg width="${size}" height="${size}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 11H16V13H0V11Z" fill="${fillColor}"/>
  </svg>`;
}

/**
 * Transformer icon.
 */
export function transformerSVG(
  strokeColor = DEFAULT_STROKE_COLOR,
  fillColor = 'none',
  size = 16,
): string {
  return `
<svg ${SVG} width="${size}" height="${size}" viewBox="0 0 16 16" fill="none">
  <circle cy="8" cx="8" r="8" fill="${fillColor}"></circle>
  <circle cx="8" cy="6" r="3.5" stroke="${strokeColor}"/>
  <circle cx="8" cy="10" r="3.5" stroke="${strokeColor}"/>
  <line x1="8" y1="3" x2="8" y2="-1" stroke="${strokeColor}" stroke-width="2"/>
  <line x1="8" y1="17" x2="8" y2="13" stroke="${strokeColor}" stroke-width="2"/>
</svg>`;
}

/**
 * Capacitor bank icon.
 */
export function capacitorBankSVG(
  strokeColor = DEFAULT_STROKE_COLOR,
  fillColor = 'none',
  size = 16,
): string {
  return `
<svg ${SVG} width="${size}" height="${size}" viewBox="0 0 16 16" fill="none">
  <circle cy="8" cx="8" r="8" fill="${fillColor}"></circle>
  <line x1="14" y1="6.5" x2="2" y2="6.5" stroke="${strokeColor}"/>
  <line x1="8" y1="17" x2="8" y2="7" stroke="${strokeColor}" stroke-width="2"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M12 0C12 2.20914 10.2091 4 8 4C5.79086 4 4 2.20914 4 0H3C3 2.76142 5.23858 5 8 5C10.7614 5 13 2.76142 13 0H12Z" fill="${strokeColor}"/>
</svg>`;
}

/**
 * Reactors icon.
 */
export function reactorsSVG(
  strokeColor = DEFAULT_STROKE_COLOR,
  fillColor = 'none',
  size = 16,
): string {
  const linePaths = [
    'M10.3032 8.96155C10.3564 8.76543 10.3848 8.55912 10.3848 8.34618C10.3848 7.05044 9.33436 6.00003 8.03861 6.00003C6.74287 6.00003 5.69246 7.05044 5.69246 8.34618C5.69246 8.55912 5.72083 8.76543 5.77399 8.96155L6.84102 8.96155C6.74606 8.77712 6.69246 8.56791 6.69246 8.34618C6.69246 7.60272 7.29515 7.00003 8.03861 7.00003C8.78207 7.00003 9.38477 7.60272 9.38477 8.34618C9.38477 8.56791 9.33116 8.77712 9.2362 8.96155L10.3032 8.96155Z',
    'M13.9956 8.96155C14.0488 8.76543 14.0771 8.55912 14.0771 8.34618C14.0771 7.05044 13.0267 6.00003 11.731 6.00003C10.4352 6.00003 9.38484 7.05044 9.38484 8.34618C9.38484 8.55912 9.41321 8.76543 9.46637 8.96155L10.5334 8.96155C10.4384 8.77712 10.3848 8.56791 10.3848 8.34618C10.3848 7.60272 10.9875 7.00003 11.731 7.00003C12.4745 7.00003 13.0771 7.60272 13.0771 8.34618C13.0771 8.56791 13.0235 8.77712 12.9286 8.96155L13.9956 8.96155Z',
    'M6.61085 8.96155C6.66401 8.76543 6.69238 8.55912 6.69238 8.34618C6.69238 7.05044 5.64197 6.00003 4.34623 6.00003C3.05048 6.00003 2.00008 7.05044 2.00008 8.34618C2.00008 8.55912 2.02844 8.76543 2.08161 8.96155L3.14864 8.96155C3.05368 8.77712 3.00008 8.56791 3.00008 8.34618C3.00008 7.60272 3.60277 7.00003 4.34623 7.00003C5.08969 7.00003 5.69238 7.60272 5.69238 8.34618C5.69238 8.56791 5.63878 8.77712 5.54382 8.96155L6.61085 8.96155Z',
  ]
    .map(
      (path: string) =>
        `<path fill-rule="evenodd" clip-rule="evenodd" d="${path}" fill="${strokeColor}"/>`,
    )
    .join('');

  return `
<svg ${SVG} width="${size}" height="${size}" viewBox="0 0 16 16" fill="none">
  <circle cy="8" cx="8" r="8" fill="${fillColor}"></circle>
  <line x1="3" y1="8.5" x2="-4.37114e-08" y2="8.5" stroke="${strokeColor}"/>
  <line x1="16" y1="8.5" x2="13" y2="8.5" stroke="${strokeColor}"/>
  ${linePaths}
</svg>`;
}

/** Energy storage icon. */
export function energyStorageSVG(
  strokeColor = DEFAULT_STROKE_COLOR,
  fillColor = 'none',
  size = 16,
): string {
  const linePath =
    'M9 18C6.48333 18 4.35 17.6167 2.6 16.85C0.866667 16.0667 0 15.1167 0 14V4C0 2.9 0.875 1.95833 2.625 1.175C4.39167 0.391665 6.51667 -1.43051e-06 9 -1.43051e-06C11.4833 -1.43051e-06 13.6 0.391665 15.35 1.175C17.1167 1.95833 18 2.9 18 4V14C18 15.1167 17.125 16.0667 15.375 16.85C13.6417 17.6167 11.5167 18 9 18ZM9 6.025C10.4833 6.025 11.975 5.81667 13.475 5.4C14.975 4.96667 15.8167 4.50833 16 4.025C15.8167 3.54167 14.975 3.08333 13.475 2.65C11.9917 2.21667 10.5 2 9 2C7.48333 2 5.99167 2.21667 4.525 2.65C3.075 3.06667 2.23333 3.525 2 4.025C2.23333 4.525 3.075 4.98333 4.525 5.4C5.99167 5.81667 7.48333 6.025 9 6.025ZM9 11C9.7 11 10.375 10.9667 11.025 10.9C11.675 10.8333 12.2917 10.7417 12.875 10.625C13.475 10.4917 14.0333 10.3333 14.55 10.15C15.0833 9.96667 15.5667 9.75833 16 9.525V6.525C15.5667 6.75833 15.0833 6.96667 14.55 7.15C14.0333 7.33333 13.475 7.49167 12.875 7.625C12.2917 7.74167 11.675 7.83333 11.025 7.9C10.375 7.96667 9.7 8 9 8C8.3 8 7.61667 7.96667 6.95 7.9C6.28333 7.83333 5.65 7.74167 5.05 7.625C4.46667 7.49167 3.91667 7.33333 3.4 7.15C2.88333 6.96667 2.41667 6.75833 2 6.525V9.525C2.41667 9.75833 2.88333 9.96667 3.4 10.15C3.91667 10.3333 4.46667 10.4917 5.05 10.625C5.65 10.7417 6.28333 10.8333 6.95 10.9C7.61667 10.9667 8.3 11 9 11ZM9 16C9.76667 16 10.5417 15.9417 11.325 15.825C12.125 15.7083 12.8583 15.5583 13.525 15.375C14.1917 15.175 14.75 14.9583 15.2 14.725C15.65 14.475 15.9167 14.225 16 13.975V11.525C15.5667 11.7583 15.0833 11.9667 14.55 12.15C14.0333 12.3333 13.475 12.4917 12.875 12.625C12.2917 12.7417 11.675 12.8333 11.025 12.9C10.375 12.9667 9.7 13 9 13C8.3 13 7.61667 12.9667 6.95 12.9C6.28333 12.8333 5.65 12.7417 5.05 12.625C4.46667 12.4917 3.91667 12.3333 3.4 12.15C2.88333 11.9667 2.41667 11.7583 2 11.525V14C2.08333 14.25 2.34167 14.4917 2.775 14.725C3.225 14.9583 3.78333 15.175 4.45 15.375C5.11667 15.5583 5.85 15.7083 6.65 15.825C7.45 15.9417 8.23333 16 9 16Z';

  return `
<svg ${SVG} width="${size}" height="${size}" viewBox="0 0 18 18" fill="none">
  <circle cy="8" cx="8" r="8" fill="${fillColor}"></circle>
  <path d="${linePath}" fill="${strokeColor}"/>
</svg>`;
}

function buildSVG(shape: string, sizex: number, sizey = sizex): string {
  return `<svg ${SVG} width="${sizex}" height="${sizey}">${shape}</svg>`;
}

/**
 * Builds a Google Maps Icon object from an SVG string.
 */
export function buildIconFromSVG(svg: string, anchor?: google.maps.Point): google.maps.Icon {
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    anchor,
  };
}

/**
 * Builds a Google Maps Icon object from an SVG string.
 */
export function buildIcon(shape: string, sizex: number, sizey = sizex): google.maps.Icon {
  const svgIcon = encodeURIComponent(buildSVG(shape, sizex, sizey));

  return {
    url: `data:image/svg+xml;charset=UTF-8,${svgIcon}`,
  };
}

/** Max congestion probability icon. */
export function maxCongestionProbabilitySVG(
  strokeColor = COLORS.NewRed.get(50),
  fillColor = COLORS.NewRed.get(95),
): string {
  const path = 'M5 38L2.61325 43H8.38675L6 38H5ZM5 26L5 38.5H6L6 26H5Z';

  return `
    <svg ${SVG} viewBox="0 0 11 43" fill="none">
      <rect x="0.5" y="0.5" width="10" height="25" fill="${fillColor}" stroke="${strokeColor}" />
      <path d="${path}" fill="${strokeColor}" />
    </svg>`;
}
